import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useProductReadContext } from '@app/context/ProductContext'

import { RadioProps } from '@ui/components/Radio/Radio'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import {
  WalletRestrictions,
  isDepositEnabledFromAction,
  isRestricted,
  isWalletSuspended,
} from '@utils/wallet.utils'

import { TickmillProductType } from '@model/TickmillProductType'
import { WalletDto, WalletTypeEnum } from '@model/WalletDto'

export const useDepositOptions = (
  wallets: WalletDto[] | undefined,
  restrictionType = WalletRestrictions.DEPOSIT,
  disableByRestrictions?: boolean
) => {
  const { t } = useTranslation()
  const loc = useLocation()

  const fetched = useWalletRestrictions()
  const providedWallets = wallets ?? fetched.wallets
  const isLoading = wallets ? false : fetched.isLoading

  const defaultDisable = disableByRestrictions ?? restrictionType === WalletRestrictions.DEPOSIT

  const walletLabelSuffix = loc.pathname.includes('introducing-broker')
    ? t('Wallet.IB Wallet')
    : t('Wallet.Wallet')

  const allOptions = providedWallets

  const radioOptions: RadioProps[] = useMemo(
    () =>
      allOptions.map((opt) => {
        const label = `${(opt as WalletDto).currency?.id} ${walletLabelSuffix}`

        const disabled = defaultDisable
          ? !isWalletAllowed(opt as WalletDto, restrictionType)
          : false

        return {
          label,
          value: opt.id,
          disabled,
        } satisfies RadioProps
      }),
    [walletLabelSuffix, t, restrictionType, defaultDisable, allOptions]
  )

  return { radioOptions, allOptions, isLoading }
}

const isWalletAllowed = (w: WalletDto, r: WalletRestrictions) => {
  const blocked = isWalletSuspended(w.restrictions) || isRestricted(r, w.restrictions)
  if (r === WalletRestrictions.DEPOSIT) {
    return !blocked && isDepositEnabledFromAction(w.depositAction)
  }
  return !blocked
}

export const useWalletRestrictions = () => {
  const location = useLocation()
  const apiClient = useApiClient(ClientApiClient)
  const { product } = useProductReadContext()

  const walletTypeId = useMemo(() => {
    if (product === TickmillProductType.ETD) return WalletTypeEnum.ETD
    if (location.pathname.includes('introducing-broker')) return WalletTypeEnum.IB
    if (location.pathname.includes('payment-agent')) return WalletTypeEnum.PAYMENT_AGENT
    return WalletTypeEnum.CFD
  }, [product, location.pathname])

  const { data: wallets = [], isLoading } = useQuery({
    queryKey: queryKeys.wallet.byType(walletTypeId),
    queryFn: () => apiClient.getWallets({ walletTypeId }),
  })

  return { wallets, isLoading }
}
